import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import Layout from "../../../components/atoms/Layout";
import { NewFooter } from "../../../components";
import { getStaticImg } from "../../../utils/function";
import analytics from "../../../utils/analytics";
import { useUserData } from "../../../store/user";
import {
  GOLDEN_TICKET_FAQS,
  GOLDEN_TICKET_WA,
} from "../../../data/goldenTicketFaq";

// Golden Ticket landing. Desktop: Figma ndHDAtyocefq8dZ5G4hrnj node 9071:23055
// (1440; sizes are % of that frame so it scales down to lg). Mobile (< lg):
// Figma Z0ACokwhXykTvhvZlbNbZz node 9170:37985 (375).
const NAVY = "#1F2D3D";

const HIGHLIGHTS = [
  "Di setiap kota hanya akan terpilih Top 6 kategori SMP dan 12 kategori SMA sesuai Leaderboard",
  "Ranking dihitung dari total kartu perdana by.U yang terjual dan teraktivasi atas nama sekolahmu.",
  "Sekolah perlu menjaga performa sampai penutupan periode leaderbord Golden Ticket.",
];

const STEPS = [
  {
    title: "Daftarkan sekolah",
    desc: "Registrasi sekolahmu untuk ikutan rangkaian Piala by.U 2026 dengan Login di Website Piala by.U",
  },
  {
    title: "Beli dan Aktifkan Kartu by.U",
    desc: "Beli kartu perdana by.U yang terjual dan teraktivasi atas nama sekolahmu.",
  },
  {
    title: "Pantau Leaderboard",
    desc: "Cek peringkat sekolahmu di leaderboard dengan Login ke dashboard Piala by.U.",
  },
  {
    title: "Amankan Golden Ticket",
    desc: "6 SMP dan 12 SMA teratas akan menerima Golden Ticket di sisa periode pendaftaran tanpa biaya.",
  },
];

// Teks FAQ-nya dipakai bareng sama section leaderboard di dashboard.
const FAQS = GOLDEN_TICKET_FAQS;

// Display title: Inter ExtraBold on mobile, Owners Narrow Black on desktop (per
// the two Figma frames). Figma pairs a gradient fill with stacked hard shadows,
// but CSS paints text-shadow over a bg-clip-text gradient — so the shadows live
// on an aria-hidden copy underneath and the gradient copy sits on top. Same
// box + font means both copies wrap identically.
const Title = ({ as: Tag = "h2", className = "", children }) => (
  <Tag
    className={`relative m-0 font-['Inter',sans-serif] font-extrabold lg:whitespace-pre-line lg:font-['Owners_Narrow',Gilroy,sans-serif] lg:font-black lg:tracking-[0.08px] ${className}`}
  >
    <span
      aria-hidden="true"
      className="absolute inset-0 text-transparent [text-shadow:0_7px_0_#26455b,0_2px_0_#3d9cb6] lg:[text-shadow:0_9px_0_#26455b,0_3px_0_#3d9cb6]"
    >
      {children}
    </span>
    <span className="relative block bg-gradient-to-b from-white to-[#d6e9f8] bg-clip-text text-transparent">
      {children}
    </span>
  </Tag>
);

// Section titles: 36/39 on mobile; 40px at 1440 (2.78vw), capped above that.
const SECTION_TITLE =
  "text-[36px] leading-[39px] lg:text-[2.78vw] min-[1440px]:text-[40px]";

const GoldenTicket = () => {
  // Zero-height marker at the very top: once it scrolls out of view the page
  // is scrolled, so the top bar picks up the HomePressCon navbar blur.
  const { ref: topRef, inView: atTop } = useInView({ initialInView: true });
  const { accessToken } = useUserData();
  const navigate = useNavigate();
  // Back arrow (as on Login): return to the previous in-app page, or home when
  // landed directly. BrowserRouter keeps the stack index in history.state.
  const goBack = () =>
    window.history.state?.idx > 0 ? navigate(-1) : navigate("/");

  // Safari iOS paints html/body behind its translucent status bar + toolbar.
  // HomePressCon gets a dark body from the global NavBar (city "press-con"),
  // but the NavBar is hidden here — so tint them navy while mounted.
  useEffect(() => {
    const els = [document.documentElement, document.body];
    els.forEach((el) => (el.style.backgroundColor = NAVY));
    return () => els.forEach((el) => (el.style.backgroundColor = ""));
  }, []);

  return (
    <div
      className="min-h-[100dvh]"
      style={{
        backgroundColor: NAVY,
        // Section backgrounds, swapped per breakpoint via Tailwind below:
        // desktop = one silk crop behind hero → steps; mobile = per-section
        // silk (Figma stretches it + dark multiply overlay, baked into webp).
        "--gt-bg-d": `url(${getStaticImg("golden-ticket/bg-d.webp")})`,
        "--gt-hero-m": `url(${getStaticImg("golden-ticket/hero-bg-m.webp")})`,
        "--gt-steps-m": `url(${getStaticImg("golden-ticket/steps-bg-m.webp")})`,
      }}
    >
      {/* Top bar: logo only (Figma: mobile 72px dark bar, desktop 88px with
        logo at x116). Desktop is transparent at the top; both get the
        HomePressCon navbar blur once scrolled. */}
      <header
        className={`fixed inset-x-0 top-0 z-[100] bg-[rgba(7,20,30,0.8)] transition-colors duration-300 ${
          atTop ? "lg:bg-transparent" : "backdrop-blur-[4px] lg:bg-[#1f2d3d60]"
        }`}
      >
        <div className="mx-auto max-w-[1440px]">
          <div className="flex h-[72px] items-center gap-[4px] px-[20px] lg:h-[88px] lg:gap-[8px] lg:pl-[8.06%]">
            {/* 44px tap target; -ml lines the arrow's ink (2px inset in the
              24px svg) up with the gutter. The logo's box center sits high
              (tall script "U"), so nudge the arrow down to the "by." x-height
              center (69% of logo height) — 16/20px visual gap to the logo. */}
            <button
              type="button"
              aria-label="Kembali"
              onClick={goBack}
              className="-ml-[12px] flex translate-y-[7px] cursor-pointer border-0 bg-transparent p-[10px] lg:translate-y-[10px]"
            >
              <img
                src={getStaticImg("icon/sa-arrow-left.svg")}
                alt=""
                width={24}
                height={24}
              />
            </button>
            <Link to="/" className="inline-block">
              <img
                src={getStaticImg("illustration/byU-logo.svg")}
                alt="by.U"
                className="block h-auto w-[57px] lg:w-[80px]"
              />
            </Link>
          </div>
        </div>
      </header>
      <Layout scrollSnapType="none" height="100dvh">
        <div ref={topRef} />
        {/* Desktop: silk BG spans top bar → steps; FAQ + footer sit on navy. */}
        <div className="pt-[72px] lg:bg-[image:var(--gt-bg-d)] lg:bg-cover lg:bg-top lg:bg-no-repeat lg:pb-[20px] lg:pt-[88px]">
          <div className="mx-auto max-w-[1440px]">
            {/* HERO */}
            <section className="relative bg-[image:var(--gt-hero-m)] bg-[length:100%_100%] bg-no-repeat px-[20px] pb-[48px] pt-[42px] lg:bg-none lg:px-0 lg:pb-[54px] lg:pt-[72px]">
              <div className="flex flex-col items-start gap-[20px] lg:ml-[10.28%] lg:w-[47.22%]">
                <span className="rounded-full bg-[rgba(255,255,255,0.13)] px-[12px] py-[7px] text-[10px] font-bold leading-[normal] text-white lg:bg-[#1f2d3d] lg:text-[12px]">
                  PROGRAM GOLDEN TICKET PIALA BY.U
                </span>
                <Title
                  as="h1"
                  className="text-[50px] leading-[49px] lg:text-[5.83vw] lg:leading-[0.99] min-[1440px]:text-[84px]"
                >
                  {"GOLDEN TICKET\nPIALA BY.U 2026"}
                </Title>
                <p className="mb-0 text-[16px] leading-[1.5] text-[#d9e4ec] lg:max-w-[86.8%] lg:text-[18px]">
                  Beli kartu perdana by.U bareng sekolahmu, puncaki leaderboard,
                  dan amankan Golden Ticket untuk berlaga di Piala by.U 2026.
                </p>
                {/* One pre-tilted art for both breakpoints (Figma 9150:1290).
                    Mobile overhangs the 20px gutters slightly (343px in a 335px
                    column); desktop sits right of the copy. */}
                <img
                  src={getStaticImg("golden-ticket/ticket.webp")}
                  alt="Golden Ticket Piala by.U 2026"
                  width={343}
                  height={187}
                  className="block h-auto w-[102.5%] max-w-none self-center lg:absolute lg:right-[9.98%] lg:top-[101px] lg:w-[33.7%]"
                />
                <div className="flex w-full flex-col gap-[10px] lg:w-auto lg:flex-row lg:gap-[12px]">
                  <a
                    href={GOLDEN_TICKET_WA}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      analytics.sendEvent({ event: "golden_ticket_click" })
                    }
                    className="flex h-[44px] w-full items-center justify-center rounded-[40px] bg-gradient-to-b from-[#f2b61a] to-[#c97d33] px-[16px] text-[16px] font-bold leading-[24px] tracking-[0.08px] text-white no-underline lg:w-[220px]"
                  >
                    Ikut Golden Ticket
                  </a>
                  {/* Leaderboard lives on the logged-in dashboard ("/") */}
                  <Link
                    to={accessToken ? "/" : "/login"}
                    onClick={() =>
                      analytics.sendEvent({ event: "leaderboard_goldenticket" })
                    }
                    className="flex h-[44px] w-full items-center justify-center rounded-[40px] border-[1px] border-solid border-white bg-transparent px-[16px] text-[16px] font-bold leading-[24px] tracking-[0.08px] text-white no-underline lg:w-[210px]"
                  >
                    Lihat Leaderboard
                  </Link>
                </div>
              </div>
            </section>

            {/* KUOTA FINAL */}
            <section className="flex flex-col items-center gap-[24px] bg-[#07324a] px-[20px] py-[56px] lg:mt-[10px] lg:flex-row lg:justify-center lg:gap-[3.33%] lg:bg-transparent lg:px-0 lg:py-[52px]">
              {/* Same art on both: mobile Figma (9201:1398) crops it to a 3:2
                  card from the top; desktop (9150:1292) shows it whole. */}
              <img
                src={getStaticImg("golden-ticket/mascot-ticket.webp")}
                alt="Maskot Piala by.U membawa Golden Ticket dan trofi"
                width={546}
                height={545}
                className="aspect-[335/224] h-auto w-full max-w-[546px] rounded-[16px] object-cover object-top lg:aspect-auto lg:w-[37.92%] lg:rounded-none"
              />
              <div className="flex w-full flex-col items-start gap-[18px] lg:w-[38.89%] lg:gap-[24px]">
                <span className="rounded-full bg-gradient-to-b from-[#f2b61a] to-[#c97d33] px-[12px] py-[7px] font-['Inter',sans-serif] text-[11px] font-bold leading-[normal] tracking-[4px] text-[#0a1825] lg:text-[12px] lg:font-normal lg:leading-[16px] lg:tracking-[6px]">
                  KUOTA GOLDEN TICKET
                </span>
                <Title className="text-[36px] leading-[39px] lg:text-[2.78vw] lg:leading-[1.375] min-[1440px]:text-[40px]">
                  {"TOP LEADERBOARD, LOLOS PIALA BY.U BEBAS BIAYA PENDAFTARAN"}
                </Title>
                <p className="mb-0 text-[14px] leading-[19px] tracking-[0.08px] text-[#d9e4ec] lg:text-[20px] lg:leading-[28px] lg:tracking-normal">
                  Kejar peringkat teratas di leaderboard Golden Ticket kotamu
                  dan dapatkan kesempatan melaju ke babak kualifikasi Piala by.U
                  2026 tanpa biaya pendaftaran!
                </p>
                {/* Mobile Figma stacks the dot above each line; desktop inline */}
                <ul className="mb-0 flex list-none flex-col gap-[18px] pl-0 lg:gap-[16px]">
                  {HIGHLIGHTS.map((text) => (
                    <li
                      key={text}
                      className="flex flex-col items-start gap-[12px] lg:flex-row lg:items-center"
                    >
                      <img
                        src={getStaticImg("golden-ticket/dot.svg")}
                        alt=""
                        width={8}
                        height={8}
                        className="size-[7px] shrink-0 lg:size-[8px]"
                      />
                      <span className="text-[14px] leading-[19px] tracking-[0.08px] text-white lg:text-[20px] lg:leading-[28px] lg:tracking-normal">
                        {text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* CARA IKUT */}
            <section className="flex flex-col gap-[24px] bg-[image:var(--gt-steps-m)] bg-[length:100%_100%] bg-no-repeat px-[20px] py-[56px] lg:mx-auto lg:mt-[10px] lg:w-[81.53%] lg:bg-none lg:px-0 lg:py-[57px]">
              <Title
                className={`${SECTION_TITLE} lg:leading-[2.075] whitespace-pre md:whitespace-pre-line lg:!whitespace-normal`}
              >
                {`CARA IKUT\nGOLDEN TICKET`}
              </Title>
              <ol className="mb-0 grid list-none grid-cols-1 gap-[24px] pl-0 sm:grid-cols-2 lg:grid-cols-4 lg:gap-[16px]">
                {STEPS.map((step, i) => (
                  <li
                    key={step.title}
                    className="flex flex-col gap-[10px] rounded-[16px] border-[1px] border-solid border-white/15 bg-[rgba(12,24,36,0.8)] p-[17px] lg:gap-[12px] lg:bg-black/35 lg:p-[20px]"
                  >
                    <span className="flex size-[38px] items-center justify-center rounded-full bg-[#f2b61a] font-['Inter',sans-serif] text-[12px] font-bold text-[#0a1825] lg:font-['Gilroy',sans-serif] lg:text-[16px]">
                      {i + 1}
                    </span>
                    <p className="mb-0 text-[16px] font-bold leading-[24px] tracking-[0.08px] text-white">
                      {step.title}
                    </p>
                    <p className="mb-0 text-[13px] leading-[20px] text-[#d9e4ec]">
                      {step.desc}
                    </p>
                  </li>
                ))}
              </ol>
            </section>
          </div>
        </div>

        {/* FAQ — native <details>: collapsed by default, one open at a time via `name` */}
        <section className="mx-auto flex max-w-[1440px] flex-col gap-[20px] px-[20px] py-[56px] lg:w-[80.97%] lg:gap-[16px] lg:px-0 lg:pb-[46px] lg:pt-[36px] min-[1440px]:w-[1166px]">
          <Title className={`${SECTION_TITLE} lg:leading-[2.075]`}>
            FAQ Golden Ticket
          </Title>
          <div className="flex flex-col gap-[20px] lg:gap-[12px]">
            {FAQS.map(({ q, a }) => (
              <details
                key={q}
                name="faq-golden-ticket"
                // Opening one closes the other (shared `name`); track opens only.
                onToggle={(e) =>
                  e.currentTarget.open &&
                  analytics.sendEvent({ event: "faq_goldenticket", title: q })
                }
                className="group rounded-[16px] border-[1px] border-solid border-[#0397c9] bg-[rgba(12,18,25,0.4)] p-[15px] lg:rounded-[24px] lg:p-[16px] lg:shadow-[0_6px_16px_rgba(201,166,43,0.07)]"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-[16px] text-[13px] font-bold leading-[20px] text-white [&::-webkit-details-marker]:hidden">
                  {q}
                  {/* by.U chevron, same as GalleryDetail Dropdown */}
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                    className="shrink-0 transition-transform group-open:rotate-180"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M3.79503 5.52656C3.53698 5.26851 3.11859 5.26851 2.86053 5.52656C2.60248 5.78461 2.60248 6.203 2.86053 6.46106L4.26228 7.8628C4.52033 8.12086 4.93872 8.12086 5.19677 7.8628C5.45483 7.60475 5.45483 7.18636 5.19677 6.92831L3.79503 5.52656ZM13.1401 6.46105C13.3982 6.20299 13.3982 5.7846 13.1401 5.52655C12.8821 5.26849 12.4637 5.26849 12.2056 5.52655L8.00039 9.73178L7.06589 8.79729C6.80783 8.53923 6.38945 8.53923 6.13139 8.79729C5.87334 9.05534 5.87334 9.47373 6.13139 9.73178L7.53314 11.1335C7.79119 11.3916 8.20958 11.3916 8.46763 11.1335L13.1401 6.46105Z"
                      fill="white"
                    />
                  </svg>
                </summary>
                <p className="mb-0 mt-[8px] text-[13px] leading-[20px] text-[#d9e4ec] lg:text-white">
                  {a}
                </p>
              </details>
            ))}
          </div>
        </section>

        <NewFooter />
      </Layout>
    </div>
  );
};

export default GoldenTicket;
